import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Variable } from "../../models/interactions/Variable";
import { getSuggestionContext } from "../variables/useVariableIntellisense.hook";
import { createVariableChipElement } from "./RichTextHtmlUtils";

interface UseRichTextIntellisenseProps {
  enabled: boolean;
  variables: Variable[];
  editorRef: RefObject<HTMLDivElement>;
  /** Called after a chip is inserted so the controller can persist the value. */
  onInserted: () => void;
}

/**
 * Variable intellisense for the contentEditable rich text editor. Mirrors
 * useVariableIntellisense.hook.ts, but the trigger query is detected on the
 * caret's current text node (chip boundaries naturally terminate it) and
 * insertion replaces the "$query" range with a non-editable chip span.
 */
export default function useRichTextIntellisense({
  enabled,
  variables,
  editorRef,
  onInserted,
}: UseRichTextIntellisenseProps) {
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const suggestionMenuRef = useRef<HTMLDivElement>(null);
  const targetNodeRef = useRef<Text | null>(null);
  const replaceRangeRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const filteredSuggestions = useMemo(() => {
    if (!enabled || !isSuggestionOpen) return [];
    const normalizedQuery = suggestionQuery.toLowerCase();
    return variables.filter((variable) =>
      variable.name.toLowerCase().startsWith(normalizedQuery)
    );
  }, [enabled, isSuggestionOpen, suggestionQuery, variables]);

  const closeSuggestionMenu = useCallback(() => {
    setIsSuggestionOpen(false);
    setSelectedSuggestionIndex(0);
    setSuggestionQuery("");
    targetNodeRef.current = null;
  }, []);

  const updateSuggestionState = useCallback(() => {
    if (!enabled) {
      closeSuggestionMenu();
      return;
    }

    const selection = window.getSelection();
    const editor = editorRef.current;
    if (!selection || !editor || selection.rangeCount === 0 || !selection.isCollapsed) {
      closeSuggestionMenu();
      return;
    }

    const { anchorNode, anchorOffset } = selection;
    if (
      !anchorNode ||
      anchorNode.nodeType !== Node.TEXT_NODE ||
      !editor.contains(anchorNode)
    ) {
      closeSuggestionMenu();
      return;
    }

    const context = getSuggestionContext(anchorNode.textContent ?? "", anchorOffset);
    if (!context) {
      closeSuggestionMenu();
      return;
    }

    targetNodeRef.current = anchorNode as Text;
    replaceRangeRef.current = { start: context.replaceStart, end: context.replaceEnd };
    setSuggestionQuery(context.query);
    setIsSuggestionOpen(true);
    setSelectedSuggestionIndex(0);
  }, [enabled, editorRef, closeSuggestionMenu]);

  const insertVariable = useCallback(
    (variable: Variable) => {
      const node = targetNodeRef.current;
      const editor = editorRef.current;
      if (!node || !editor || !editor.contains(node)) {
        closeSuggestionMenu();
        return;
      }

      const { start, end } = replaceRangeRef.current;
      const range = document.createRange();
      range.setStart(node, Math.min(start, node.length));
      range.setEnd(node, Math.min(end, node.length));
      range.deleteContents();

      const chip = createVariableChipElement(variable);
      range.insertNode(chip);

      // The caret needs a text node after the non-editable chip to land in; a
      // zero-width space keeps it reachable and is stripped on serialization.
      let after = chip.nextSibling;
      if (!after || after.nodeType !== Node.TEXT_NODE) {
        after = document.createTextNode("\u200B");
        chip.parentNode?.insertBefore(after, chip.nextSibling);
      }

      const selection = window.getSelection();
      if (selection) {
        const caretRange = document.createRange();
        const offset = (after.textContent ?? "").startsWith("\u200B") ? 1 : 0;
        caretRange.setStart(after, Math.min(offset, (after as Text).length));
        caretRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(caretRange);
      }

      closeSuggestionMenu();
      onInserted();
    },
    [editorRef, closeSuggestionMenu, onInserted]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!enabled || !isSuggestionOpen || filteredSuggestions.length === 0) {
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev + 1) % filteredSuggestions.length);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev === 0 ? filteredSuggestions.length - 1 : prev - 1
        );
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const selectedVariable =
          filteredSuggestions[selectedSuggestionIndex] ?? filteredSuggestions[0];
        if (selectedVariable) {
          insertVariable(selectedVariable);
        }
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        closeSuggestionMenu();
      }
    },
    [
      enabled,
      isSuggestionOpen,
      filteredSuggestions,
      selectedSuggestionIndex,
      insertVariable,
      closeSuggestionMenu,
    ]
  );

  useEffect(() => {
    if (!isSuggestionOpen) return;
    if (filteredSuggestions.length === 0) {
      closeSuggestionMenu();
      return;
    }

    if (selectedSuggestionIndex >= filteredSuggestions.length) {
      setSelectedSuggestionIndex(0);
    }
  }, [isSuggestionOpen, filteredSuggestions, selectedSuggestionIndex, closeSuggestionMenu]);

  useEffect(() => {
    if (!isSuggestionOpen) return;
    const suggestionMenu = suggestionMenuRef.current;
    if (!suggestionMenu) return;

    const selectedElement = suggestionMenu.querySelector<HTMLElement>(
      `[data-suggestion-index="${selectedSuggestionIndex}"]`
    );

    if (selectedElement?.scrollIntoView) {
      selectedElement.scrollIntoView({ block: "nearest" });
    }
  }, [isSuggestionOpen, selectedSuggestionIndex, filteredSuggestions.length]);

  return {
    isSuggestionOpen,
    selectedSuggestionIndex,
    filteredSuggestions,
    suggestionMenuRef,
    closeSuggestionMenu,
    updateSuggestionState,
    insertVariable,
    handleKeyDown,
  };
}
